<template>
    <div class="row">
        <div v-if="editing" class="col-12 mb-1">
            <div class="alert alert-warning py-50 mb-0 small">
                <feather-icon icon="AlertTriangleIcon" size="14" class="mr-25" />
                Düzenleme modunda yalnızca sürücü, KM, personel ve açıklama bilgileri değiştirilebilir.
                Miktar, yakıt tipi, transfer tipi veya araç/depo değişikliği için kaydı silip yeniden oluşturun.
            </div>
        </div>

        <div class="col-12 mb-1">
            <label class="form-label">İşlem No</label>
            <input type="text" class="form-control" :value="form.ProcessCode || 'Otomatik Oluşturulacak'" disabled />
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Transfer Tipi <span class="text-danger">*</span></label>
            <v-select-list
                v-model="form.IsFromVehicleToVehicle"
                :options="transferTypes"
                label="text"
                :reduce="(option) => option.value"
                placeholder="Seçiniz..."
                :disabled="editing"
            />
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Yakıt Tipi <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="FuelTypeId" :watch="form.FuelTypeId">
                <v-select-list
                    v-model="form.FuelTypeId"
                    :options="fuelTypes"
                    label="VisibleName"
                    :reduce="(option) => option.Id"
                    placeholder="Seçiniz..."
                    :disabled="editing"
                />
            </v-validator>
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Transfer Miktarı (Litre) <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="Quantity" :watch="form.Quantity">
                <input type="number" class="form-control" v-model="form.Quantity" step="0.01" @focus="onQuantityFocus" :disabled="editing" />
            </v-validator>
        </div>

        <!-- ─── KAYNAK ARAÇ ─────────────────────────────────────── -->
        <div class="col-12"><hr class="my-50" /><small class="text-muted font-weight-bold">KAYNAK ARAÇ</small></div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Kaynak Araç Plakası <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="FromVehiclePlateId" :watch="form.FromVehiclePlateId">
                <v-select-list
                    v-model="form.FromVehiclePlateId"
                    :options="vehiclePlates"
                    label="PlateValue"
                    :reduce="(option) => option.Id"
                    placeholder="Seçiniz..."
                    @input="onFromPlateSelect"
                    :disabled="editing"
                />
            </v-validator>
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">
                Kaynak Araç KM
                <b-spinner v-if="fromSeyirLoading" small class="ml-50" />
            </label>
            <input
                type="number"
                class="form-control"
                v-model="form.FromVehicleCurrentKM"
                :disabled="fromKmLocked"
                placeholder="KM giriniz..."
            />
            <small v-if="fromKmStatus.message" :class="`text-${fromKmStatus.variant}`">
                {{ fromKmStatus.message }}
            </small>
        </div>

        <div class="col-md-6 mb-1" v-if="form.FromVehicleSeyirDriverFullName">
            <label class="form-label text-primary">Seyir Sürücüsü (Kaynak)</label>
            <input type="text" class="form-control" :value="form.FromVehicleSeyirDriverFullName" disabled />
        </div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Sürücü (Kaynak) <span class="text-muted small">— opsiyonel, değiştirilebilir</span></label>
            <v-select-list
                v-model="form.FromVehicleDriverEmployeeId"
                :options="employees"
                label="FullName"
                :reduce="(option) => option.Id"
                placeholder="Sürücü seçiniz..."
                :clearable="true"
            />
        </div>

        <!-- ─── HEDEF: ARAÇ veya DEPO ──────────────────────────── -->
        <div class="col-12"><hr class="my-50" /><small class="text-muted font-weight-bold">{{ form.IsFromVehicleToVehicle ? 'HEDEF ARAÇ' : 'HEDEF DEPO' }}</small></div>

        <div class="col-md-6 mb-1" v-if="form.IsFromVehicleToVehicle">
            <label class="form-label">Hedef Araç Plakası <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="ToVehiclePlateId" :watch="form.ToVehiclePlateId">
                <v-select-list
                    v-model="form.ToVehiclePlateId"
                    :options="vehiclePlates"
                    label="PlateValue"
                    :reduce="(option) => option.Id"
                    placeholder="Seçiniz..."
                    @input="onToPlateSelect"
                    :disabled="editing"
                />
            </v-validator>
        </div>

        <div class="col-md-6 mb-1" v-if="form.IsFromVehicleToVehicle">
            <label class="form-label">
                Hedef Araç KM
                <b-spinner v-if="toSeyirLoading" small class="ml-50" />
            </label>
            <input
                type="number"
                class="form-control"
                v-model="form.ToVehicleCurrentKM"
                :disabled="toKmLocked"
                placeholder="KM giriniz..."
            />
            <small v-if="toKmStatus.message" :class="`text-${toKmStatus.variant}`">
                {{ toKmStatus.message }}
            </small>
        </div>

        <template v-if="form.IsFromVehicleToVehicle">
            <div class="col-md-6 mb-1" v-if="form.ToVehicleSeyirDriverFullName">
                <label class="form-label text-primary">Seyir Sürücüsü (Hedef)</label>
                <input type="text" class="form-control" :value="form.ToVehicleSeyirDriverFullName" disabled />
            </div>

            <div class="col-md-6 mb-1">
                <label class="form-label">Sürücü (Hedef) <span class="text-muted small">— opsiyonel, değiştirilebilir</span></label>
                <v-select-list
                    v-model="form.ToVehicleDriverEmployeeId"
                    :options="employees"
                    label="FullName"
                    :reduce="(option) => option.Id"
                    placeholder="Sürücü seçiniz..."
                    :clearable="true"
                />
            </div>
        </template>

        <div class="col-md-6 mb-1" v-if="!form.IsFromVehicleToVehicle">
            <label class="form-label">Hedef Depo <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="ToFuelStorageId" :watch="form.ToFuelStorageId">
                <v-select-list
                    v-model="form.ToFuelStorageId"
                    :options="filteredFuelStorages"
                    label="DisplayName"
                    :reduce="(option) => option.Id"
                    placeholder="Seçiniz..."
                    :disabled="editing"
                />
            </v-validator>
        </div>

        <!-- ─── DİĞER BİLGİLER ─────────────────────────────────── -->
        <div class="col-12"><hr class="my-50" /></div>

        <div class="col-md-6 mb-1">
            <label class="form-label">Transfer Eden Personel <span class="text-danger">*</span></label>
             <v-validator :errors="errors" parameter="ResponsibleEmployeeId" :watch="form.ResponsibleEmployeeId">
                <v-select-list
                    v-model="form.ResponsibleEmployeeId"
                    :options="employees"
                    label="FullName"
                    :reduce="(option) => option.Id"
                    placeholder="Seçiniz..."
                />
            </v-validator>
        </div>

        <div class="col-12 mb-1">
            <label class="form-label">Transfer Sebebi / Açıklama</label>
             <v-validator :errors="errors" parameter="Description" :watch="form.Description">
                <textarea class="form-control" v-model="form.Description" rows="3" placeholder="Transfer sebebini açıklayın..."></textarea>
            </v-validator>
        </div>
    </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue';
import systemDataApi from "@/api/system-data";
import EmployeeOperationsApi from "@/api/employee-operations";
import SeyirOperationsApi from "@/api/seyir";
import VehicleOperationsApi from "@/api/vehicle-operations";
import api from "@/api/fuel-storage";

export default {
    components: { BSpinner },
    props: {
        value: {
            type: Object,
            default: () => ({})
        },
        errors: {
            type: Object,
            default: () => ({})
        },
        editing: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: this.value,
            transferTypes: [
                { value: true, text: 'Araçtan Araca' },
                { value: false, text: 'Araçtan Depoya' }
            ],
            vehiclePlates: [],
            fuelTypes: [],
            fuelStorages: [],
            employees: [],
            isUpdatingFromProp: false,
            fromSeyirLoading: false,
            toSeyirLoading: false,
            fromKmLocked: false,
            toKmLocked: false,
            fromKmStatus: { message: null, variant: null },
            toKmStatus: { message: null, variant: null },
        };
    },
    computed: {
        filteredFuelStorages() {
            if (!this.form.FuelTypeId) return [];
            return this.fuelStorages
                .filter(s => s.FuelStorageFuelTypeId === this.form.FuelTypeId)
                .map(s => ({
                    ...s,
                    DisplayName: `${s.Name} [${s.CapacityStr || '0'} Lt]`
                }));
        }
    },
    watch: {
        value: {
            handler(val) {
                this.isUpdatingFromProp = true;
                this.form = val;
                this.$nextTick(() => { this.isUpdatingFromProp = false; });
            },
            deep: true
        },
        form: {
            handler(val) {
                this.$emit('input', val);
            },
            deep: true
        },
        'form.FuelTypeId': function(newVal, oldVal) {
             if (this.isUpdatingFromProp) return;
             if (newVal !== oldVal) {
                 this.$set(this.form, 'ToFuelStorageId', null);
             }
        },
        'form.IsFromVehicleToVehicle': function(newVal) {
            if (!newVal) {
                // Araçtan Depoya: Hedef araç alanlarını temizle
                this.$set(this.form, 'ToVehiclePlateId', null);
                this.$set(this.form, 'ToVehicleCurrentKM', null);
                this.$set(this.form, 'ToVehicleDriverEmployeeId', null);
                this.$set(this.form, 'ToVehicleSeyirDriverFullName', null);
                this.$set(this.form, 'ToVehicleSeyirDriverId', null);
                this.$set(this.form, 'ToVehicleSeyirDriverKey', null);
                this.toKmLocked = false;
                this.toKmStatus = { message: null, variant: null };
            }
        }
    },
    mounted() {
        this.loadVehiclePlates();
        this.loadFuelTypes();
        this.loadEmployees();
        this.loadFuelStorages();
    },
    methods: {
        async loadVehiclePlates() {
            // Yakıt Çıkışı ekranında çalışan KM/Seyir akışıyla aynı
            // VehicleOperationsApi.Plate.ListAll endpoint'ini kullanıyoruz; iki ekran
            // arasında shape/permission farkı bulunmasın diye.
            try {
                const response = await VehicleOperationsApi.Plate.ListAll();
                if (response.data.Success) {
                    this.vehiclePlates = response.data.Data || [];
                }
            } catch (error) {
                console.error("Vehicle plates loading error", error);
            }
        },
        async loadFuelTypes() {
            try {
                const response = await systemDataApi.FuelType(true);
                if (response.data.Success) {
                    this.fuelTypes = response.data.Data || [];
                }
            } catch (error) {
                console.error("Error loading fuel types:", error);
            }
        },
        async loadEmployees() {
            try {
                const response = await EmployeeOperationsApi.Employee.List(false);
                if (response.data.Success) {
                    this.employees = response.data.Data || [];
                }
            } catch (error) {
                console.error("Employees loading error", error);
            }
        },
        async loadFuelStorages() {
            try {
                const response = await api.FuelStorage.List();
                if (response.data.Success) {
                    this.fuelStorages = response.data.Data || [];
                }
            } catch (error) {
                console.error("Fuel storages loading error", error);
            }
        },
        onQuantityFocus(e) {
            if (this.form.Quantity === 0 || this.form.Quantity === '0') {
                this.form.Quantity = null;
            }
            e.target.select();
        },

        // ─── SEYİR ENTEGRASYONU ───────────────────────────────────
        onFromPlateSelect(plateId) {
            this.$set(this.form, 'FromVehicleCurrentKM', null);
            this.$set(this.form, 'FromVehicleSeyirDriverFullName', null);
            this.$set(this.form, 'FromVehicleSeyirDriverId', null);
            this.$set(this.form, 'FromVehicleSeyirDriverKey', null);
            this.fromKmLocked = false;
            this.fromKmStatus = { message: null, variant: null };

            if (!plateId) return;
            this.fetchSeyirData(plateId, 'from');
        },
        onToPlateSelect(plateId) {
            this.$set(this.form, 'ToVehicleCurrentKM', null);
            this.$set(this.form, 'ToVehicleSeyirDriverFullName', null);
            this.$set(this.form, 'ToVehicleSeyirDriverId', null);
            this.$set(this.form, 'ToVehicleSeyirDriverKey', null);
            this.toKmLocked = false;
            this.toKmStatus = { message: null, variant: null };

            if (!plateId) return;
            this.fetchSeyirData(plateId, 'to');
        },
        async fetchSeyirData(plateId, side) {
            const plate = this.vehiclePlates.find(p => p.Id === plateId);
            if (!plate || !plate.PlateValue) return;

            if (side === 'from') this.fromSeyirLoading = true;
            else this.toSeyirLoading = true;

            try {
                const response = await SeyirOperationsApi.ActualLocation({ Plate: plate.PlateValue });
                const dataList = response.data.Data;
                let latest = null;
                if (response.data.Success && Array.isArray(dataList) && dataList.length > 0) {
                    latest = dataList.sort((a, b) => new Date(b.DataTime) - new Date(a.DataTime))[0];
                }

                if (side === 'from') {
                    if (latest && latest.TotalKm && parseFloat(latest.TotalKm) > 0) {
                        this.$set(this.form, 'FromVehicleCurrentKM', parseFloat(latest.TotalKm));
                        this.fromKmLocked = true;
                    } else {
                        this.fromKmStatus = { message: 'Araç KM bilgisine erişilemedi, manuel girebilirsiniz.', variant: 'warning' };
                    }
                    if (latest) {
                        this.$set(this.form, 'FromVehicleSeyirDriverFullName', latest.Driver || null);
                        this.$set(this.form, 'FromVehicleSeyirDriverId', latest.DriverId || null);
                        this.$set(this.form, 'FromVehicleSeyirDriverKey', latest.DriverKey || null);
                    }
                } else {
                    if (latest && latest.TotalKm && parseFloat(latest.TotalKm) > 0) {
                        this.$set(this.form, 'ToVehicleCurrentKM', parseFloat(latest.TotalKm));
                        this.toKmLocked = true;
                    } else {
                        this.toKmStatus = { message: 'Araç KM bilgisine erişilemedi, manuel girebilirsiniz.', variant: 'warning' };
                    }
                    if (latest) {
                        this.$set(this.form, 'ToVehicleSeyirDriverFullName', latest.Driver || null);
                        this.$set(this.form, 'ToVehicleSeyirDriverId', latest.DriverId || null);
                        this.$set(this.form, 'ToVehicleSeyirDriverKey', latest.DriverKey || null);
                    }
                }
            } catch (err) {
                const status = { message: 'Seyir servisine ulaşılamadı.', variant: 'danger' };
                if (side === 'from') this.fromKmStatus = status;
                else this.toKmStatus = status;
            } finally {
                if (side === 'from') this.fromSeyirLoading = false;
                else this.toSeyirLoading = false;
            }
        }
    }
};
</script>
